/**
 * Secure error logging utility
 * Logs detailed errors in development, sanitized errors in production
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Logs error with context awareness
 * @param {string} context - Context/component name where error occurred
 * @param {Error} error - The error object
 * @param {object} metadata - Additional metadata (optional)
 */
export const logError = (context, error, metadata = {}) => {
  if (isDevelopment) {
    // Development: Log full details
    console.error(`[${context}] Error:`, error);
    if (Object.keys(metadata).length > 0) {
      console.error(`[${context}] Metadata:`, metadata);
    }
  } else {
    // Production: Generic message only
    console.error(`An error occurred in ${context}`);

    // Send to error tracking service (if available)
    if (window.errorTracker?.captureException) {
      window.errorTracker.captureException(error, {
        context,
        ...metadata
      });
    }
  }
};

/**
 * Logs warning with context awareness
 * @param {string} context - Context/component name
 * @param {string} message - Warning message
 * @param {object} metadata - Additional metadata (optional)
 */
export const logWarning = (context, message, metadata = {}) => {
  if (isDevelopment) {
    console.warn(`[${context}] Warning:`, message);
    if (Object.keys(metadata).length > 0) {
      console.warn(`[${context}] Metadata:`, metadata);
    }
  } else {
    console.warn(`Warning in ${context}`);

    // Send to monitoring service
    if (window.errorTracker?.captureMessage) {
      window.errorTracker.captureMessage(message, 'warning', {
        context,
        ...metadata
      });
    }
  }
};

/**
 * Creates user-friendly error message from Error object
 * Sanitizes technical details for production
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error) => {
  if (isDevelopment) {
    return error.message || 'An error occurred';
  }

  // Production: Generic messages based on error type
  if (error.name === 'TypeError') {
    return 'A technical error occurred. Please try again.';
  }

  if (error.name === 'NetworkError' || error.message?.includes('fetch')) {
    return 'Unable to connect to the server. Please check your connection.';
  }

  if (error.status === 401 || error.status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (error.status === 404) {
    return 'The requested resource was not found.';
  }

  if (error.status >= 500) {
    return 'A server error occurred. Please try again later.';
  }

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Logs fetch/API errors with proper context
 * @param {string} context - API context (e.g., 'fetchProblems')
 * @param {Response} response - Fetch response object
 * @param {Error} error - Error object (optional)
 */
export const logApiError = async (context, response, error = null) => {
  const metadata = {
    status: response?.status,
    statusText: response?.statusText,
    url: response?.url
  };

  if (isDevelopment && response) {
    try {
      const body = await response.clone().text();
      metadata.responseBody = body.substring(0, 500); // Limit size
    } catch (e) {
      // Ignore if body already consumed
    }
  }

  logError(context, error || new Error(`API Error: ${response?.status}`), metadata);
};

const errorLogging = {
  logError,
  logWarning,
  getUserFriendlyErrorMessage,
  logApiError
};

export default errorLogging;
